import {
  UseMutateFunction,
  UseQueryResult,
  useMutation,
  useQuery
} from "@tanstack/react-query";
import rentersApi, {
  MUTATION_SYNC_SUBSCRIPTIONS,
  QUERY_SYNC_SUBSCRIPTIONS_STATUS
} from "api/landlord/renters";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useState
} from "react";

import { getErrorMessage } from "utils/error";
import { PropertyManagementSystemSyncStatus } from "shared/types";
import addressApi, {
  MUTATION_SYNC_RENTERS,
  QUERY_SYNC_RENTERS_STATUS
} from "api/landlord/address";
import { prepareAlgoliaData } from "views/renters/utils";
import { EnrollmentOption } from "types/landlord/enrollment";
import { useAuthContext } from "providers/Auth";
import { useUploadCsvController } from "./slices/UploadCsvSlice/controller";
import {
  CsvUploadStatus,
  CsvUploadStatusResponse
} from "./slices/UploadCsvSlice/types";
import { toast } from "react-toastify";
import { instance } from "api/apiBase";

type SubscriptionQueue = {
  isSyncing: boolean;
  amount: number;
};

type ProgressContext = {
  subscriptionsQueue: SubscriptionQueue;
  pmsStatus: PropertyManagementSystemSyncStatus | undefined;
  handleRequestPMSsync: UseMutateFunction;
  setSubscriptionsQueue: Dispatch<SetStateAction<SubscriptionQueue>>;
  syncSubscriptions: UseMutateFunction<
    void,
    Error,
    {
      renters: any;
      enrollmentOption: EnrollmentOption;
    },
    unknown
  >;
  uploadCsv: {
    meta?: UseQueryResult<void | CsvUploadStatusResponse | undefined, Error>;
    status: CsvUploadStatus | "loading";
    canUpload: boolean;
    cancelAnalysis?: UseMutateFunction<void, Error, void, unknown>;
  };
};

const ProgressContext = createContext<ProgressContext>({
  // Sync PMS feature
  pmsStatus: undefined,
  handleRequestPMSsync: () => {},
  // Sync Subscriptions feature
  syncSubscriptions: () => {},
  setSubscriptionsQueue: () => {},
  subscriptionsQueue: {
    isSyncing: false,
    amount: 0
  },
  uploadCsv: {
    status: CsvUploadStatus.Parsing,
    canUpload: false
  }
});

export function ProgressProvider({ children }: { children: ReactNode }) {
  const { auth, partner, type } = useAuthContext();
  const {
    uploadMeta: uploadCSVMeta,
    canUpload: canUploadCsv,
    csvUploadStatus
  } = useUploadCsvController();

  const [subscriptionsQueue, setSubscriptionsQueue] =
    useState<SubscriptionQueue>({
      isSyncing: true,
      amount: 0
    });

  const [pmsStatus, setPmsStatus] =
    useState<PropertyManagementSystemSyncStatus>({ status: "running" });

  const { refetch: refetchPmsStatus } =
    useQuery<PropertyManagementSystemSyncStatus>({
      queryKey: [QUERY_SYNC_RENTERS_STATUS],
      queryFn: () =>
        addressApi
          .getPropertyManagementSystemSyncStatus("partner")
          .then((res) => {
            setPmsStatus(res);
            if (res) return res;
            else return true;
          })
          .catch(() => {
            setPmsStatus({ status: "error" });
          }),
      refetchInterval: 4000,
      enabled:
        Boolean(partner?.algolia?.client_search_key) &&
        type.isPMS &&
        pmsStatus.status === "running" &&
        Boolean(auth?.partner.status === "active")
    });

  const { mutate: handleRequestPMSsync } = useMutation({
    mutationFn: async () => {
      return addressApi
        .requestPropertyManagementSystemSync("partner")
        .then(() =>
          addressApi.requestPropertyManagementSystemSync("enrollments")
        )
        .then(() => {
          toast.success("Sync partners requested successfully");
          setPmsStatus((prev) => ({ ...prev, status: "running" }));
          refetchPmsStatus();
        })
        .catch((error) => {
          toast.error(getErrorMessage(error));
          refetchPmsStatus();
        });
    },
    mutationKey: [MUTATION_SYNC_RENTERS]
  });

  const { mutate: cancelAnalysis } = useMutation({
    mutationKey: ["cancelAnalysis", uploadCSVMeta.data?.id],
    mutationFn: async () => {
      return instance
        .post(`/portal/v1/csv/${uploadCSVMeta.data.id}/cancel`)
        .then(() => {
          toast.success("Analysis cancelled successfully");
        })
        .catch((error) => {
          toast.error(getErrorMessage(error));
        });
    }
  });

  useQuery<{ enrollments_enqueued: number }>({
    queryKey: [QUERY_SYNC_SUBSCRIPTIONS_STATUS],
    queryFn: () =>
      rentersApi
        .getEnrollmentsQueue()
        .then((res) => {
          if (res.data.enrollments_enqueued === 0)
            setSubscriptionsQueue((prev) => ({ ...prev, isSyncing: false }));
          else
            setSubscriptionsQueue({
              isSyncing: true,
              amount: res.data.enrollments_enqueued
            });

          return res.data;
        })
        .catch(() => {
          setSubscriptionsQueue((prev) => ({
            ...prev,
            isSyncing: false
          }));
        }),
    refetchInterval: 3000,
    enabled: Boolean(
      subscriptionsQueue.isSyncing &&
        Boolean(auth?.partner) &&
        Boolean(auth?.partner.status === "active")
    )
  });

  const { mutate: syncSubscriptions } = useMutation({
    mutationKey: [MUTATION_SYNC_SUBSCRIPTIONS],
    mutationFn: async ({
      renters,
      enrollmentOption
    }: {
      renters: any;
      enrollmentOption: EnrollmentOption;
    }) => {
      const modifiedData = prepareAlgoliaData(renters, enrollmentOption);

      const confirm = type.isBoomOps
        ? window.confirm(
            `You're about to enroll ${modifiedData.length} renters? (This is BoomOps only message)`
          )
        : true;

      if (!confirm)
        return Promise.reject().finally(() => {
          toast.error("Operation cancelled");
        });

      return await rentersApi
        .enrollAllRenters(modifiedData)
        .then(() => {
          toast.success("Subscription requested successfully");
          setSubscriptionsQueue((prev) => ({ ...prev, isSyncing: true }));
        })
        .catch((error) => {
          toast.error(getErrorMessage(error));
          setSubscriptionsQueue((prev) => ({ ...prev, isSyncing: false }));
        });
    }
  });

  return (
    <ProgressContext.Provider
      value={{
        setSubscriptionsQueue,
        subscriptionsQueue,
        syncSubscriptions,
        pmsStatus,
        handleRequestPMSsync,
        uploadCsv: {
          meta: uploadCSVMeta,
          canUpload: canUploadCsv,
          status: csvUploadStatus,
          cancelAnalysis: cancelAnalysis
        }
      }}
    >
      {children}
    </ProgressContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useProgressContext() {
  return useContext(ProgressContext);
}
